import { useState, type ReactNode } from 'react';
import {
  Avatar,
  Badge,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  BriefcaseRegular,
  CloudRegular,
  HistoryRegular,
  KeyRegular,
  MailRegular,
  PeopleRegular,
  PeopleTeamRegular,
  PersonRegular,
  ShieldRegular,
} from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import { UserPicker } from '../features/users/UserPicker';
import { formatBytes } from '../util/bytes';
import type { DirectoryUserDto, InsightItemDto } from '../api/types';
import './insights.css';

function Section({ icon, title, count, children }: { icon: ReactNode; title: string; count?: number; children: ReactNode }) {
  return (
    <div className="ins-card">
      <div className="ins-card-title">
        {icon}
        {title}
        {typeof count === 'number' ? <span className="ins-count">{count}</span> : null}
      </div>
      {children}
    </div>
  );
}

function Kv({ k, v, pii }: { k: string; v?: string | null; pii?: boolean }) {
  return (
    <div className="ins-kv">
      <span className="ins-kv-key">{k}</span>
      <span className={pii ? 'ins-kv-val pii' : 'ins-kv-val'}>{v || '—'}</span>
    </div>
  );
}

function StringList({ items, empty }: { items: string[]; empty: string }) {
  if (items.length === 0) return <Text className="ins-muted">{empty}</Text>;
  return (
    <div className="ins-list">
      {items.map((s, i) => (
        <div className="ins-row" key={i}>
          <span className="ins-row-main">{s}</span>
        </div>
      ))}
    </div>
  );
}

function ItemList({
  items,
  empty,
  hrefFor,
}: {
  items: InsightItemDto[];
  empty: string;
  hrefFor?: (it: InsightItemDto) => string;
}) {
  if (items.length === 0) return <Text className="ins-muted">{empty}</Text>;
  return (
    <div className="ins-list">
      {items.map((it) => (
        <div className="ins-row" key={it.id}>
          {hrefFor ? (
            <a className="ins-row-main ins-link" href={hrefFor(it)} target="_blank" rel="noopener noreferrer">
              {it.displayName}
            </a>
          ) : (
            <span className="ins-row-main">{it.displayName}</span>
          )}
          {it.secondary ? <span className="ins-row-sub">{it.secondary}</span> : null}
        </div>
      ))}
    </div>
  );
}

function OneDriveSection({ upn }: { upn: string }) {
  const api = useApi();
  const s = useAsync(() => api.getOneDriveUser(upn), [api, upn]);
  if (s.error) return null;
  const a = s.data?.account;
  return (
    <Section icon={<CloudRegular />} title="OneDrive">
      {s.loading ? (
        <Spinner size="tiny" label="Lade…" />
      ) : a ? (
        <>
          <Kv k="Genutzt" v={`${formatBytes(a.storageUsedBytes)} von ${formatBytes(a.storageAllocatedBytes)}`} />
          <Kv k="Auslastung" v={`${a.percentUsed}%`} />
          <Kv k="Dateien" v={a.fileCount.toLocaleString('de-DE')} />
          <Kv k="Letzte Aktivität" v={a.lastActivity ? new Date(a.lastActivity).toLocaleDateString('de-DE') : '—'} />
        </>
      ) : (
        <Kv k="Status" v={s.data?.note ?? 'Keine OneDrive-Daten.'} />
      )}
    </Section>
  );
}

function InsightsDetail({ userId }: { userId: string }) {
  const api = useApi();
  const state = useAsync(() => api.getUserInsights(userId), [api, userId]);

  if (state.loading) return <Spinner size="small" label="Lade Insights…" />;
  if (state.error) {
    return (
      <MessageBar intent="error">
        <MessageBarBody>{state.error.message}</MessageBarBody>
      </MessageBar>
    );
  }
  const d = state.data;
  if (!d) return null;
  const p = d.profile;

  const kpis: { label: string; value: number }[] = [
    { label: 'Lizenzen', value: d.licenses.length },
    { label: 'Entra-Rollen', value: d.adminRoles.length },
    { label: 'ARM-Rollen', value: d.armRoles.length },
    { label: 'Teams', value: d.teams.length },
    { label: 'Group-Member', value: d.groupMemberships.length },
    { label: 'Owner', value: d.groupOwnerships.length },
  ];

  return (
    <>
      <div className="ins-profile">
        <Avatar
          name={p.displayName}
          image={p.photoDataUrl ? { src: p.photoDataUrl } : undefined}
          size={72}
          color="colorful"
        />
        <div className="ins-profile-main">
          <div className="ins-name-row">
            <span className="ins-name pii">{p.displayName}</span>
            <Badge appearance="tint" color={p.accountEnabled ? 'success' : 'danger'}>
              {p.accountEnabled ? 'aktiv' : 'gesperrt'}
            </Badge>
          </div>
          <span className="ins-upn pii">{p.userPrincipalName}</span>

          <div className="ins-infobar">
            <div className="ins-meta">
              {p.jobTitle ? (
                <span className="ins-meta-item">
                  <BriefcaseRegular />
                  {p.jobTitle}
                  {p.department ? ` · ${p.department}` : ''}
                </span>
              ) : null}
              {p.mail ? (
                <span className="ins-meta-item pii">
                  <MailRegular />
                  {p.mail}
                </span>
              ) : null}
              {p.managerName ? (
                <span className="ins-meta-item pii">
                  <PersonRegular />
                  Manager: {p.managerName}
                </span>
              ) : null}
            </div>

            <div className="ins-kpis">
              {kpis.map((k) => (
                <div className="ins-kpi" key={k.label}>
                  <span className="ins-kpi-num">{k.value}</span>
                  <span className="ins-kpi-label">{k.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="ins-cat">Details</div>
      <div className="ins-grid">
        <Section icon={<PersonRegular />} title="Details">
          <Kv k="Vorname" v={p.givenName} pii />
          <Kv k="Nachname" v={p.surname} pii />
          <Kv k="E-Mail" v={p.mail} pii />
          <Kv k="Abteilung" v={p.department} />
          <Kv k="Büro" v={p.officeLocation} />
          <Kv k="Mobil" v={p.mobilePhone} pii />
        </Section>

        <Section icon={<HistoryRegular />} title="Aktivität">
          <Kv k="Erstellt am" v={d.activity.createdDateTime} />
          <Kv
            k="Letzte Anmeldung"
            v={d.activity.signInAvailable ? d.activity.lastSignInDateTime : 'n/v (AuditLog.Read.All)'}
          />
        </Section>

        <Section icon={<KeyRegular />} title="Lizenzen" count={d.licenses.length}>
          <StringList items={d.licenses} empty="Keine Lizenzen zugewiesen." />
        </Section>

        <OneDriveSection upn={p.userPrincipalName} />
      </div>

      <div className="ins-cat">Admin</div>
      <div className="ins-grid">
        <Section icon={<ShieldRegular />} title="Entra-ID-Rollen" count={d.adminRoles.length}>
          <StringList items={d.adminRoles} empty="Keine administrativen Rollen." />
        </Section>

        <Section icon={<CloudRegular />} title="ARM-Rollen (Subscriptions)" count={d.armRoles.length}>
          <ItemList items={d.armRoles} empty="Keine Azure-RBAC-Rollen (oder keine ARM-Leserechte)." />
        </Section>
      </div>

      <div className="ins-cat">Mitgliedschaften</div>
      <div className="ins-grid">
        <Section icon={<PeopleTeamRegular />} title="Teams-Mitgliedschaften" count={d.teams.length}>
          <ItemList
            items={d.teams}
            empty="Keine Teams."
            hrefFor={(it) => `/teams?ids=${encodeURIComponent(it.id)}`}
          />
        </Section>

        <Section icon={<PeopleRegular />} title="Gruppen-Mitgliedschaften" count={d.groupMemberships.length}>
          <ItemList
            items={d.groupMemberships}
            empty="Keine Gruppen-Mitgliedschaften."
            hrefFor={(it) => `/groups/members?ids=${encodeURIComponent(it.id)}`}
          />
        </Section>

        <Section icon={<PersonRegular />} title="Gruppen-Besitz (Owner)" count={d.groupOwnerships.length}>
          <ItemList
            items={d.groupOwnerships}
            empty="Besitzt keine Gruppen."
            hrefFor={(it) => `/groups/members?ids=${encodeURIComponent(it.id)}`}
          />
        </Section>
      </div>
    </>
  );
}

export function UserInsightsPage() {
  const [selected, setSelected] = useState<DirectoryUserDto | undefined>();

  return (
    <div>
      <PageHeader
        title="User-Insights"
        description="Benutzer suchen — Profil, Aktivität, Lizenzen, Teams, Entra-Rollen und Gruppen auf einen Blick."
      />
      <div className="ins-layout">
        <div className="ins-search">
          <Field label="Benutzer suchen">
            <UserPicker onSelect={setSelected} placeholder="Person suchen…" />
          </Field>
        </div>

        {selected ? (
          <InsightsDetail key={selected.id} userId={selected.id} />
        ) : (
          <Text className="ins-muted">
            Wähle oben einen Benutzer, um Details, Aktivität, Lizenzen, Teams, Rollen sowie Gruppen-Mitgliedschaften und
            -Besitz zu sehen.
          </Text>
        )}
      </div>
    </div>
  );
}
